import axios from 'axios';
import { API_V1 } from '@/infra/http';
import { CognitoSessionExpiredError, getValidCognitoAuthHeader } from '@/infra/auth/cognitoToken';
import * as Types from './user.types';
import { deleteCookie, setCookie } from '@/utils';
import { ENV } from '@/config/env';

export const getUser = () =>
  API_V1.get<Types.UserResponse>(`api/views/admin/v1/users/get_user`).then((res) => res.data);

// Identity of the back-office admin, authenticated with the Cognito JWT
// `ap_cognito_authorization` — NOT with `ap_authorization` (the ecommerce login).
// Used only to gate affordances that depend on the back-office session (editing
// profile/services/location and replying to reviews). Overrides Authorization
// per-request because API_V1 does not go through AP_API's Cognito interceptor,
// which is why the token is renewed here too.
export const getAdminUser = async () => {
  const authorization = await getValidCognitoAuthHeader();

  // Without a usable token the request is a guaranteed 401, so it is not sent at all.
  // The caller sees the same thing either way (no admin identity, affordances hidden),
  // but the backend is spared a pointless unauthenticated call.
  if (!authorization) {
    throw new CognitoSessionExpiredError();
  }

  return API_V1.get<Types.UserResponse>(`api/views/admin/v1/users/get_user`, {
    headers: { Authorization: authorization },
  }).then((res) => res.data);
};

export const login = (credentials: Types.LoginRequest) =>
  axios
    .post(
      `${ENV.BACK_URL}/users/sign_in`,
      { user: credentials },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Expose-Headers': 'Authorization',
          actionLoader: 'false',
        },
      },
    )
    .then((res) => {
      setCookie('ap_authorization', res.headers.authorization, 5000);

      return res.data;
    });

export const logout = () =>
  API_V1.delete(`users/sign_out`).then((res) => {
    deleteCookie('ap_authorization');

    return res.data;
  });

export const loginWithSocialAccount = ({ device, accessToken }) =>
  API_V1.post(`/users/oauth`, { device, accessToken }).then((res) => res.data);

export const getUserByCode = ({ userCode }) =>
  API_V1.get<Types.UserResponse>(`api/views/admin/v1/users/user_with_user_code`, { params: { userCode } }).then(
    (res) => res.data,
  );
